import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import styled, { useTheme } from 'styled-components'

import useDocumentClick from 'hooks/useDocumentClick'

import InvisibleButton from '../../../../components/display/InvisibleButton'
import MenuBurger from '../../../../components/icons/MenuBurger'

import { Divider, DisplayOnlyAtBreakpoint } from '../../common'
import { InverseOption } from '../../types'
import {
  IncompleteBookingIndicator,
  useIncompleteBooking,
} from '../../IncompleteBooking'

import AvatarWithChevron from './AvatarWithChevron'

const MenuBurgerWrapper = styled.div<{ inverse?: boolean }>`
  display: flex;
  svg {
    flex-shrink: 0;
    color: ${({ inverse, theme }) =>
      inverse ? theme['-ds-neutral-100'].value : theme['-ds-core-400'].value};
    width: 36px;
    margin: 0;
  }
`

const Trigger = styled(InvisibleButton)<InverseOption>`
  outline: none;

  color: ${({ inverse, theme }) =>
    inverse ? theme['-ds-neutral-100'].value : theme['-ds-core-300'].value};
`

export const LinkList = styled.ul`
  min-width: 240px;
  margin: 0;
  padding: 0;
  list-style: none;

  li,
  ${Divider}, ${DisplayOnlyAtBreakpoint} {
    &:not(:first-child) {
      margin-top: 16px;
    }
  }
`

const Panel = styled.div<{ open: boolean }>`
  visibility: ${({ open }) => (open === true ? 'visible' : 'hidden')};
  opacity: ${({ open }) => (open === true ? '1' : '0')};
  transition: visibility 0s, opacity 150ms linear;

  min-width: 240px;
  background: ${({ theme }) => theme['-ds-neutral-100'].value};
  border: 1px solid ${({ theme }) => theme['-ds-neutral-400'].value};
  padding: 30px;

  position: absolute;
  right: 0;
  top: 56px;
  border-radius: 6px;

  box-shadow: 0 8px 16px 2px rgb(0 0 0 / 10%);
`

const Container = styled.div`
  display: flex;
  position: relative;
`

type DropdownProps = {
  avatarUrl?: string
  signedIn: boolean
} & InverseOption

const Dropdown: React.FC<DropdownProps> = ({
  avatarUrl,
  inverse,
  signedIn,
  children,
}) => {
  const [open, setOpen] = useState(false)

  const location = useLocation()

  const theme = useTheme()

  useEffect(() => {
    setOpen(false)
  }, [location])

  const panelRef = useRef<HTMLDivElement>()
  const triggerRef = useRef<HTMLButtonElement>()

  const { showAlert: showIncompleteBooking } = useIncompleteBooking()

  const handleOutsideClick = (e: Event) => {
    const clickWasInsidePanel = panelRef.current?.contains(e.target as Node)
    const clickWasInsideTrigger = triggerRef.current?.contains(e.target as Node)

    if (!clickWasInsidePanel && !clickWasInsideTrigger) {
      setOpen(false)
    }
  }

  useDocumentClick(handleOutsideClick)

  const toggleOpen = () => setOpen(!open)

  return (
    <Container className="qa-navbar-dropdown">
      <Trigger
        ref={triggerRef}
        onClick={toggleOpen}
        inverse={inverse}
        aria-label="Open profile menu"
      >
        {signedIn ? (
          <>
            {showIncompleteBooking && (
              <IncompleteBookingIndicator
                style={{
                  right: '20px',
                  top: '29px',
                  width: '13px',
                  height: '13px',
                  border: `2px solid ${theme?.['-ds-neutral-100']?.value}`,
                  zIndex: 1,
                }}
              />
            )}
            <AvatarWithChevron avatarUrl={avatarUrl} />
          </>
        ) : (
          <MenuBurgerWrapper inverse={inverse}>
            <MenuBurger />
          </MenuBurgerWrapper>
        )}
      </Trigger>

      <Panel ref={panelRef} open={open}>
        {children}
      </Panel>
    </Container>
  )
}

export default Dropdown
